import { useState, useEffect, useContext } from "react";
import { Row, Col } from "react-bootstrap";
import { useLocation } from "react-router";
import {
  StatusWidget,
  TrendWidget,
  DisbursementsWidget,
  EnhancementWidget,
  NotationsWidget,
  CommissionApprovalWidget,
  CommissionReportingWidget,
} from "./widgets/components/widgets";
import { EnhancementModal } from "./widgets/components/modals";
import { withHeaderMessages } from "../higherOrderComponents/WithHeaderMessages";
import { WithApiAccess } from "../lib/HigherOrderComponents/WithApiAccess";
import { LoadingSpinner } from "../lib/components/LoadingSpinner";
import qs from "qs";
import "./Dashboard.css";
import { getRangeDates } from "./widgets/constants";
import { canAcceptCreditCard } from "../utils/providers";
import "primereact/resources/themes/bootstrap4-light-blue/theme.css";
import { PaymentWidget } from "./widgets/components/widgets/PaymentWidget";
import { DistributionQuoteWidget } from "./widgets/components/widgets/DistributionQuoteWidget";
import { AuthContext } from "../lib/contexts/AuthContext";

const Dashboard = (props) => {
  const [userDetails, setUserDetails] = useState(null);
  const [statusSummary, setStatusSummary] = useState([]);
  const { hasClaim } = useContext(AuthContext);

  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);
  const [isLoadingTrend, setIsLoadingTrend] = useState(true);
  const [trendRange, setTrendRange] = useState("6m");
  const [trendRaw, setTrendRaw] = useState(null);
  const [notationsData, setNotationsData] = useState(null);
  const [isLoadingNotations, setIsLoadingNotations] = useState(true);
  const [disbursementsData, setDisbursementsData] = useState(null);
  const [disbursementsRange, setDisbursementsRange] = useState("6m");
  const [isLoadingDisbursements, setIsLoadingDisbursements] = useState(true);
  const [enhanceOpen, setEnhanceOpen] = useState(false);

  const [commissionApprovalData, setCommissionApprovalData] = useState(null);
  const [isLoadingCommissionApproval, setIsLoadingCommissionApproval] =
    useState(false);
  const [commissionReportingData, setCommissionReportingData] = useState(null);
  const [commissionReportingRange, setCommissionReportingRange] =
    useState("6m");
  const [isLoadingCommissionReporting, setIsLoadingCommissionReporting] =
    useState(false);

  const [contractSearchState] = useState({
    searchTerm: "",
    page: 1,
    pageSize: 20,
  });

  const handleNavigate = (path) => {
    window.location.href = path;
  };

  const location = useLocation();

  // 1. Load User
  useEffect(() => {
    async function getUserDetails() {
      await props.api
        .get("/api/user")
        .then((res) => setUserDetails(res.data))
        .finally(() => setIsLoadingUser(false));
    }
    getUserDetails();
  }, []);

  // 2. Load Data (List and Status Summary)
  useEffect(() => {
    async function fetchData() {
      const { searchTerm, page, pageSize } = contractSearchState;
      const params = {
        Query: searchTerm,
        Options: {
          Page: page,
          ResultsPerPage: pageSize,
        },
      };
      const queryString = qs.stringify(params, {
        allowDots: true,
        addQueryPrefix: true,
        encode: false,
      });
      setIsLoadingSummary(true);
      try {
        const summaryRes = await props.api.get(
          `/api/contracts/status/search${queryString}`,
        );
        setStatusSummary(summaryRes.data || []);
      } catch (err) {
        console.error("Dashboard data fetch error", err);
      } finally {
        setIsLoadingSummary(false);
      }
    }

    fetchData();
  }, [location.search]);

  useEffect(() => {
    async function fetchTrend() {
      setIsLoadingTrend(true);
      try {
        const { startDate, endDate } = getRangeDates(trendRange);
        const res = await props.api.get(
          `/api/contracts/acceptance-trend/search?acceptedStartDate=${startDate}&acceptedEndDate=${endDate}`,
        );
        setTrendRaw(res.data?.value || []);
      } finally {
        setIsLoadingTrend(false);
      }
    }
    fetchTrend();
  }, [trendRange]);

  useEffect(() => {
    async function fetchNotations() {
      try {
        const res = await props.api.get("/api/contracts/notes/search");
        setNotationsData(res.data);
      } catch (err) {
        console.error("Failed to load notations", err);
      } finally {
        setIsLoadingNotations(false);
      }
    }
    fetchNotations();
  }, []);

  useEffect(() => {
    async function fetchDisbursements() {
      setIsLoadingDisbursements(true);
      try {
        const { startDate, endDate } = getRangeDates(disbursementsRange);
        const res = await props.api.get(
          `/api/contracts/disbursement-trend/search?disbursementStartDate=${startDate}&disbursementEndDate=${endDate}`,
        );
        setDisbursementsData(res.data);
      } finally {
        setIsLoadingDisbursements(false);
      }
    }
    fetchDisbursements();
  }, [disbursementsRange]);

  // Commission widgets — only fetched when the user has the Commissions claim
  useEffect(() => {
    if (!hasClaim("Commissions")) return;
    async function fetchCommissionApproval() {
      setIsLoadingCommissionApproval(true);
      try {
        const res = await props.api.get(
          "/api/commission/contracts?CommissionStatus=Pending",
        );
        setCommissionApprovalData(res.data);
      } catch (err) {
        console.error("Failed to load commission approval data", err);
      } finally {
        setIsLoadingCommissionApproval(false);
      }
    }
    fetchCommissionApproval();
  }, []);

  useEffect(() => {
    if (!hasClaim("Commissions")) return;
    async function fetchCommissionReporting() {
      setIsLoadingCommissionReporting(true);
      try {
        const { startDate, endDate } = getRangeDates(commissionReportingRange);
        const res = await props.api.get(
          `/api/commission/reporting/search?startDate=${startDate}&endDate=${endDate}`,
        );
        setCommissionReportingData(res.data);
      } catch (err) {
        console.error("Failed to load commission reporting data", err);
      } finally {
        setIsLoadingCommissionReporting(false);
      }
    }
    fetchCommissionReporting();
  }, [commissionReportingRange]);

  const canShowPayment = (userDetails) =>
    userDetails?.customers?.length > 0 &&
    userDetails?.acceptCreditCard &&
    canAcceptCreditCard(userDetails.customers);

  if (isLoadingUser) return <LoadingSpinner />;

  return (
    <div className="cp-dashboard">
      <Row className="cp-dashboard__grid">
        <Col xs={12} lg={6}>
          <StatusWidget
            summary={statusSummary}
            isLoading={isLoadingSummary}
            onNavigate={handleNavigate}
          />
        </Col>
        <Col xs={12} lg={6}>
          <TrendWidget
            data={trendRaw}
            range={trendRange}
            onRangeChange={setTrendRange}
            isLoading={isLoadingTrend}
            onNavigate={handleNavigate}
            onDownload={() => {}}
          />
        </Col>
        <Col xs={12}>
          <NotationsWidget
            data={notationsData}
            isLoading={isLoadingNotations}
            onNavigate={handleNavigate}
          />
        </Col>
        <Col xs={12}>
          <DisbursementsWidget
            data={disbursementsData}
            range={disbursementsRange}
            onRangeChange={setDisbursementsRange}
            isLoading={isLoadingDisbursements}
            onNavigate={handleNavigate}
          />
        </Col>
        {hasClaim("Commissions") && (
          <Col xs={12} lg={6}>
            <CommissionApprovalWidget
              data={commissionApprovalData}
              isLoading={isLoadingCommissionApproval}
              onNavigate={handleNavigate}
            />
          </Col>
        )}
        {hasClaim("Commissions") && (
          <Col xs={12} lg={6}>
            <CommissionReportingWidget
              data={commissionReportingData}
              range={commissionReportingRange}
              onRangeChange={setCommissionReportingRange}
              isLoading={isLoadingCommissionReporting}
              onNavigate={handleNavigate}
            />
          </Col>
        )}
        <Col xs={12} lg={6}>
          <EnhancementWidget onOpen={() => setEnhanceOpen(true)} />
        </Col>
        {canShowPayment(userDetails) && (
          <Col xs={12} lg={6}>
            <PaymentWidget />
          </Col>
        )}
        <EnhancementModal
          show={enhanceOpen}
          onHide={() => setEnhanceOpen(false)}
        />
        {hasClaim("Quotes") && (
          <Col xs={12} lg={6}>
            <DistributionQuoteWidget />
          </Col>
        )}
      </Row>
    </div>
  );
};

export default withHeaderMessages(WithApiAccess(Dashboard));
